import * as React from 'react';
import { useEffect } from 'react';
import { genericErrorLogger } from '@enginuity-common/services/errorLoggingService';
import {
  ErrorBoundary,
  ErrorFallback
} from '@enginuity-apps-ui-components/other/error-handling';
import Header from '@enginuity-apps-ui-components/organisms/header/header';
import {
  Router,
  Switch,
  Route,
  Redirect,
  BrowserRouter
} from 'react-router-dom';
import Register from './pages/registration/register';
import Engage from './pages/onboarding/engage';
import AuthCallback from './pages/auth/callback';
import AuthInit from './pages/auth/init';
import SignIn from './pages/auth/signIn';
import SetNewPassword from './pages/registration/set-new-password';
import { createBrowserHistory } from 'history';
import ManageAccount from './pages/account/manage-account';
import { useState } from 'react';
import { IUserProfile } from '@enginuity-common/types/user';
import { sendEvent, addEventHandler, removeEventHandler } from '@enginuity-common/utils/events';
import { getStoredToken, mapUser, Token } from '@enginuity-common/utils/auth';
import { SpinnerOverlay } from '@enginuity-apps-ui-components/atoms/spinner/spinner';
import GenericErrorMessage from './components/misc/generic-error-message';
import VerifyEmailChange from './pages/account/verify-email-change';
import * as tracking from '@enginuity-common/utils/tracking';
import ForgotPassword from './pages/auth/forgot-password';
import Signout from './pages/auth/signout';
import { AuthService } from '@enginuity-common/services/authService';
import { getUrlParameter } from '@enginuity-common/utils/url';
import { getAppNameFromDestination, getContentForApp, getSettingsForApp } from './apps';
import { ReactComponent as EalLogo } from './img/EAL_Lockup_Celestial_Blue.svg';
import Footer from './components/global/footer/footer';
import Intercom from 'react-intercom';


export default function SinglePageApp() {
  const history = createBrowserHistory();
  const [userProfile, setUserProfile] = useState<IUserProfile>();
  const [token, setToken] = useState(getStoredToken<Token>());
  const [genericError, setGenericError] = useState<string | undefined>();
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    tracking.initialize();
    tracking.logCurrentPageView();
  }, []);

  const fromUri = getUrlParameter('fromURI') || getUrlParameter('redirectUrl') || window.spConfig.postActivationDestination;
  const appName = getAppNameFromDestination(fromUri as string);

  const settingsForApp = getSettingsForApp(appName);
  const productContent = getContentForApp(appName);

  const authService = new AuthService({
    baseUrl: window.spConfig.authBaseUrl,
    clientId: window.spConfig.authClientId
  });

  const authStatusChanged = () => {
    setToken(getStoredToken<Token>());
  };

  const signOut = () => {
    authService.doLogout();
  };


  useEffect(() => {
    addEventHandler('sp-auth-changed', authStatusChanged);

    return () => {
      removeEventHandler('sp-auth-changed', authStatusChanged);
    };
  });

  if (genericError) {
    return <GenericErrorMessage errorMsg={genericError} fromUri={fromUri!}></GenericErrorMessage>
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={genericErrorLogger}
    >
      {settingsForApp.intercomEnabled && (
        <Intercom appID={window.spConfig.intercomAppId!} hide_default_launcher={false} />
      )}
      <div className='page-wrapper'>
        <div className='header-wrapper'>
          <div className="container-fluid px-md-4 px-lg-5">
            <div className="row mx-md-n4 mx-lg-n5">
              <div className="col px-md-4 px-lg-5">
                <BrowserRouter>
                  <Header
                    brandName='Enginuity'
                    appName={appName}
                    pageTitle=''
                    headerText={productContent.headerText!}
                    showAuthenticated={!!(token && userProfile)}
                    homeUrl='https://enginuity.org/'
                    userProfile={userProfile}
                    signOut={signOut}
                    setMobileNavState={() => { }}
                    logo={appName === 'EAL' ? <EalLogo /> : undefined}
                  />
                </BrowserRouter>
              </div>
            </div>
          </div>
        </div>
        <div className='body-content'>
          <main id='main'>
            <Router history={history}>
              <Switch>
                <Route path="/signin" render={() => { return <SignIn fromUri={fromUri!} settingsForApp={settingsForApp} productContent={productContent} /> }} />
                <Route path="/forgot-password" render={() => { return <ForgotPassword appName={appName} settingsForApp={settingsForApp} fromUri={fromUri!} /> }} />
                <Route path="/set-password" render={() => { return <SetNewPassword settingsForApp={settingsForApp} appName={appName} fromUri={fromUri!} isActivation={false} setPasswordContent={{ buttonText: "Reset Password" }} /> }} />

                {settingsForApp.registrationEnabled && [
                  <Route path="/register" render={() => { return <Register fromUri={fromUri!} /> }} />,
                  <Route path="/engage" render={() => { return <Engage fromUri={fromUri!} /> }} />,
                  <Route path="/activate" render={() => { return <SetNewPassword settingsForApp={settingsForApp} appName={appName} fromUri={fromUri!} isActivation={true} /> }} />,
                  <Route path="/verify-email" render={() => { return <VerifyEmailChange fromUri={fromUri!} /> }} />
                ]}

                {settingsForApp.managementEnabled && [
                  <Route path="/manage" render={() => { return <ManageAccount fromUri={fromUri!} userProfile={userProfile} onProfileChange={setUserProfile} /> }} />
                ]}

                <Route exact path="/auth" component={AuthInit} />
                <Route path="/auth/callback" component={AuthCallback} />
                <Route path="/auth/signout" component={Signout} />
                <Redirect from="/" to={`${settingsForApp.registrationEnabled ? '/register' : '/signin'}?fromURI=${fromUri}`} />
              </Switch>
            </Router>
          </main>
        </div>
      </div>

      <SpinnerOverlay active={showSpinner} />
    </ErrorBoundary >
  );
}
